import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Home } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
const contactFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(1, "Phone number is required"),
  propertyAddress: z.string().min(1, "Property address is required").max(200, "Property address must be less than 200 characters"),
  sellingProperty: z.enum(["Yes", "No", "Just Looking for Value", "Unsure"]).optional(),
  message: z.string().optional()
});
type ContactFormData = z.infer<typeof contactFormSchema>;
const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    toast
  } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: {
      errors
    }
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema)
  });
  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      console.log("Submitting contact form:", data);
      const {
        data: result,
        error
      } = await supabase.functions.invoke('send-contact-email', {
        body: data
      });
      if (error) {
        console.error("Edge function error:", error);
        throw new Error(error.message || "Failed to send message");
      }
      if (!result?.success) {
        console.error("Email sending failed:", result);
        throw new Error(result?.error || "Failed to send message");
      }
      console.log("Email sent successfully:", result);
      toast({
        title: "Message sent successfully!",
        description: "We'll get back to you within 24 hours."
      });
      reset();
    } catch (error: any) {
      console.error("Contact form submission error:", error);
      toast({
        title: "Error sending message",
        description: error.message || "Please try again or contact us directly at support@godyspo.com",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <Card className="border-godyspo-gold/30 shadow-lg hover:shadow-xl transition-all duration-300">
      <CardHeader className="bg-gradient-to-r from-godyspo-gold/10 to-blue-500/10 border-b border-godyspo-gold/20">
        <CardTitle className="flex items-center gap-2">
          <Home className="h-6 w-6 text-godyspo-gold" />
          Get Help Selling
        </CardTitle>
        <CardDescription>
          Fill out the form below and we'll get back to you soon.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input id="name" {...register("name")} placeholder="Your full name" />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" {...register("email")} placeholder="your.email@example.com" />
              {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number *</Label>
            <PhoneInput
              id="phone"
              value={watch("phone") || ""}
              onChange={(value) => setValue("phone", value)}
              showValidation={true}
            />
            {errors.phone && <p className="text-sm text-destructive">{errors.phone.message}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="propertyAddress">Property Address *</Label>
            <Input id="propertyAddress" {...register("propertyAddress")} placeholder="123 Main St, City, State" />
            {errors.propertyAddress && <p className="text-sm text-destructive">{errors.propertyAddress.message}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="sellingProperty">Are You Selling the Property?</Label>
            <Select onValueChange={value => setValue("sellingProperty", value as "Yes" | "No" | "Just Looking for Value" | "Unsure")}>
              <SelectTrigger className="bg-background">
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
                <SelectItem value="Just Looking for Value">Just Looking for Value</SelectItem>
                <SelectItem value="Unsure">Unsure</SelectItem>
              </SelectContent>
            </Select>
            {errors.sellingProperty && <p className="text-sm text-destructive">{errors.sellingProperty.message}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea id="message" {...register("message")} placeholder="Tell us more about your inquiry..." rows={5} />
            {errors.message && <p className="text-sm text-destructive">{errors.message.message}</p>}
          </div>
          
          <Button type="submit" className="w-full bg-godyspo-gold text-godyspo-background hover:bg-godyspo-gold/90" disabled={isSubmitting}>
            {isSubmitting ? "Sending..." : <>
                <Send className="h-4 w-4 mr-2" />
                Send Message
              </>}
          </Button>
        </form>
      </CardContent>
    </Card>;
};
export default ContactForm;